import React, { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// AI Pages
import OperationsPage from "./pages/AI/OperationsPage";
import AIPage from "./pages/AIPage";
import MarketingPage from "./pages/AI/MarketingPage";
import SalesPage from "./pages/AI/SalesPage";
import TelecommunicationPage from "./pages/AI/TelecommunicationPage";
import MediaEntertainmentPage from "./pages/AI/MediaEntertainmentPage";
import HealthcarePage from "./pages/AI/HealthcarePage";
import EducationPage from "./pages/AI/EducationPage";
import BankingFinancePage from "./pages/AI/BankingFinancePage";
import ManufacturingPage from "./pages/AI/Manufacturing";
import CustomerServicePage from "./pages/AI/CustomerServicePage";
import AgriculturePage from "./pages/AI/AgriculturePage";
import InsurancePage from "./pages/AI/InsurancePage";
import RetailPage from "./pages/AI/RetailPage";
import ConversationalAIPage from "./pages/AI/ConversationalAIPage";
import AISmallIndustriesPage from "./pages/AI/AISmallIndustriesPage";
import AgenticAICapabilities from "./pages/AI/AgenticAICapabilities";
import AgenticAIDomains from "./pages/AI/AgenticAIDomains";

// RPA Pages
import RPAPage from "./pages/RPAPage";
import RpaDomains from "./pages/RPA/RpaDomains";
import RPAManufacturingPage from "./pages/RPA/ManufacturingPage";
import BankingPage from "./pages/RPA/BankingPage";
import LogisticsPage from "./pages/RPA/LogisticsPage";
import RPATelecommunicationPage from "./pages/RPA/TelecommunicationPage";
import RPAMediaEntertainmentPage from "./pages/RPA/MediaEntertainmentPage";
import RPAHealthcarePage from "./pages/RPA/HealthcarePage";
import RealEstatePage from "./pages/RPA/RealEstatePage";
import TravelHospitalityPage from "./pages/RPA/TravelHospitalityPage";
import RPAEducationPage from "./pages/RPA/EducationPage";
import GovernmentPublicServicesPage from "./pages/RPA/GovernmentPublicServicesPage";
import HumanResourcePage from "./pages/RPA/HumanResourcePage";

// Main Pages
import QAPage from "./pages/QAPage";
import IndustryPage from "./pages/IndustryPage";
import BlogsPage from "./pages/BlogsPage";
import Department from "./pages/Department";

// Blog Pages
import AIAgentsPage from "./pages/blogs/AIAgentsPage";
import AutomotiveIndustryPage from "./pages/blogs/AutomotiveIndustryPage";
import FinancialSectorPage from "./pages/blogs/FinancialSectorPage";
import RetailIndustryPage from "./pages/blogs/RetailIndustryPage";
import LifeSciencesPage from "./pages/blogs/LifeSciencesPage";
import TelecomSectorPage from "./pages/blogs/TelecomSectorPage";
import AerospaceAviationPage from "./pages/blogs/AerospaceAviationPage";
import HealthcareHospitalsPage from "./pages/blogs/HealthcareHospitalsPage";
import ManufacturingIndustrialPage from "./pages/blogs/ManufacturingIndustrialPage";
import AgricultureAgritechPage from "./pages/blogs/AgricultureAgritechPage";
import EducationEdTechPage from "./pages/blogs/EducationEdTechPage";
import LogisticsSupplyChainPage from "./pages/blogs/LogisticsSupplyChainPage";
import EnergyUtilitiesPage from "./pages/blogs/EnergyUtilitiesPage";
import BlogsMediaEntertainmentPage from "./pages/blogs/MediaEntertainmentPage";
import TourismHospitalityPage from "./pages/blogs/TourismHospitalityPage";
import LegalCompliancePage from "./pages/blogs/LegalCompliancePage";
import PublicSectorGovernmentPage from "./pages/blogs/PublicSectorGovernmentPage";
import DefenseSecurityPage from "./pages/blogs/DefenseSecurityPage";
import MiningNaturalResourcesPage from "./pages/blogs/MiningNaturalResourcesPage";
import GSTRReconciliationPage from "./pages/blogs/GSTRReconciliationPage";
import SAPAutomationPage from "./pages/blogs/SAPAutomationPage";
import AITechnologiesCatalogPage from "./pages/blogs/Aitechnologiescatalogpage";

// Shared Pages
import DomainUseCasePage from "./pages/shared/DomainUseCasePage";
import DomainGroupPage from "./pages/shared/DomainGroupPage";

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    const path =
      location.pathname.replace(/\/index\.html$/i, "/") || "/";

    const isHome = path === "/";

    document.documentElement.classList.toggle(
      "agentic-route",
      !isHome
    );
  }, [location.pathname]);

  return (
    <>
      {/* The site header and footer are the static ones in public/index.html. */}
      <Routes>

        {/* ================= HOME ================= */}
        {/* The homepage is the static public/index.html; React renders nothing here. */}
        <Route path="/" element={null} />
        <Route path="/index.html" element={<Navigate to="/" replace />} />


        {/* ================= AI ================= */}
        <Route path="/ai" element={<AIPage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/telecom" element={<TelecommunicationPage />} />
        <Route path="/entertainment" element={<MediaEntertainmentPage />} />
        <Route path="/healthcare" element={<HealthcarePage />} />
        <Route path="/education" element={<EducationPage />} />
        <Route path="/banking" element={<BankingFinancePage />} />
        <Route path="/manufacturing" element={<ManufacturingPage />} />
        <Route path="/customer" element={<CustomerServicePage />} />
        <Route path="/agriculture" element={<AgriculturePage />} />
        <Route path="/insurance" element={<InsurancePage />} />
        <Route path="/retail" element={<RetailPage />} />
        <Route path="/conversational-ai" element={<ConversationalAIPage />} />
        <Route
          path="/ai-for-small-industries"
          element={<AISmallIndustriesPage />}
        />

        {/* Agentic AI */}
        <Route
          path="/agentic-ai-capabilities"
          element={<Navigate to="/agentic-ai" replace />}
        />

        <Route
          path="/agentic-ai"
          element={<AgenticAICapabilities />}
        />

        <Route
          path="/agentic-ai/:domainSlug"
          element={<AgenticAIDomains />}
        />

        <Route
          path="/agentic-ai/:domainSlug/:groupId"
          element={<DomainGroupPage />}
        />

        <Route
          path="/agentic-ai/:domainSlug/:groupId/:useCaseId"
          element={<DomainUseCasePage />}
        />


        {/* ================= RPA ================= */}

        <Route
          path="/rpa"
          element={
            <Navigate
              to="/robotic-process-automation"
              replace
            />
          }
        />

        <Route
          path="/robotic-process-automation"
          element={<RPAPage />}
        />

        <Route
          path="/rpa-use-cases"
          element={<RpaDomains />}
        />

        <Route
          path="/rpa-use-cases/:domainSlug"
          element={<RpaDomains />}
        />

        <Route
          path="/rpa-use-cases/:domainSlug/:groupId"
          element={<DomainGroupPage />}
        />

        <Route
          path="/rpa-use-cases/:domainSlug/:groupId/:useCaseId"
          element={<DomainUseCasePage />}
        />

        <Route
          path="/rpa-manufacturing"
          element={<RPAManufacturingPage />}
        />

        <Route
          path="/rpa-banking"
          element={<BankingPage />}
        />

        <Route
          path="/rpa-logistics"
          element={<LogisticsPage />}
        />

        <Route
          path="/rpa-telecommunication"
          element={<RPATelecommunicationPage />}
        />

        <Route
          path="/rpa-media-entertainment"
          element={<RPAMediaEntertainmentPage />}
        />

        <Route
          path="/rpa-healthcare"
          element={<RPAHealthcarePage />}
        />

        <Route
          path="/rpa-real-estate"
          element={<RealEstatePage />}
        />

        <Route
          path="/rpa-travel-hospitality"
          element={<TravelHospitalityPage />}
        />

        <Route
          path="/rpa-education"
          element={<RPAEducationPage />}
        />

        <Route
          path="/robotic-process-automation/government-public-services"
          element={<GovernmentPublicServicesPage />}
        />

        <Route
          path="/rpa-human-resource"
          element={<HumanResourcePage />}
        />


        {/* ================= OTHER MAIN PAGES ================= */}

        <Route
          path="/quality-assurance"
          element={<QAPage />}
        />

        <Route
          path="/industry-4.0"
          element={<IndustryPage />}
        />

        <Route
          path="/department"
          element={<Department />}
        />


        {/* ================= BLOGS ================= */}

        <Route
          path="/blogs"
          element={<BlogsPage />}
        />

        <Route
          path="/blog/ai-agents"
          element={<AIAgentsPage />}
        />

        <Route
          path="/blogs/gstr-reconciliation"
          element={<GSTRReconciliationPage />}
        />

        <Route
          path="/blogs/sap-automation"
          element={<SAPAutomationPage />}
        />

        <Route
          path="/blogs/ai-catalog"
          element={<AITechnologiesCatalogPage />}
        />

        <Route
          path="/blog/rpa-future"
          element={<div>RPA Future Page</div>}
        />

        <Route
          path="/blog/uipath-training"
          element={<div>UIPATH Training Page</div>}
        />

        <Route
          path="/blog/rpa-salary-trends"
          element={<div>RPA Salary Trends Page</div>}
        />


        {/* ================= INDUSTRY BLOGS ================= */}

        <Route
          path="/automotive-industry"
          element={<AutomotiveIndustryPage />}
        />

        <Route
          path="/financial-sector"
          element={<FinancialSectorPage />}
        />

        <Route
          path="/retail-industry"
          element={<RetailIndustryPage />}
        />

        <Route
          path="/life-sciences"
          element={<LifeSciencesPage />}
        />

        <Route
          path="/telecom-sector"
          element={<TelecomSectorPage />}
        />

        <Route
          path="/aerospace-aviation"
          element={<AerospaceAviationPage />}
        />

        <Route
          path="/healthcare-and-hospitals"
          element={<HealthcareHospitalsPage />}
        />

        <Route
          path="/manufacturing-industrial"
          element={<ManufacturingIndustrialPage />}
        />

        <Route
          path="/agriculture-agritech"
          element={<AgricultureAgritechPage />}
        />

        <Route
          path="/education-edtech"
          element={<EducationEdTechPage />}
        />

        <Route
          path="/logistics-supply-chain"
          element={<LogisticsSupplyChainPage />}
        />

        <Route
          path="/energy-utilities"
          element={<EnergyUtilitiesPage />}
        />

        <Route
          path="/media-entertainment"
          element={<BlogsMediaEntertainmentPage />}
        />

        <Route
          path="/tourism-hospitality"
          element={<TourismHospitalityPage />}
        />

        <Route
          path="/legal-compliance"
          element={<LegalCompliancePage />}
        />

        <Route
          path="/public-sector-government"
          element={<PublicSectorGovernmentPage />}
        />

        <Route
          path="/defense-security"
          element={<DefenseSecurityPage />}
        />

        <Route
          path="/mining-natural-resources"
          element={<MiningNaturalResourcesPage />}
        />

      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;